import { useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { DirectTaskState } from '@frame/lib/api/tasks/types'

import { useRootContext } from '../context'
import { useDirectTask } from './useDirectTask'
import { Item } from './useGetItems'
import { useVerifyViaCredentials } from './useVerifyViaCredentials'

export function useSelectItem() {
  const navigate = useNavigate()
  const location = useLocation()
  const { taskId = '' } = useParams()
  const { actions } = useRootContext()
  const verify = useVerifyViaCredentials()
  const task = useDirectTask(true)
  const [isLoading, setIsLoading] = useState(false)

  function getSearchQueryUrlParam(employerName: string, item?: Item): string {
    const params = new URLSearchParams(location.search)
    if (params.has('q')) {
      return params.get('q') || ''
    }
    if (item?.name) {
      return item.name
    }
    return employerName
  }

  async function handleUnskipTask(taskId: string) {
    await actions.unSkipDirectTask(taskId)
  }

  async function handleUpdateTask(taskId: string, employerName: string, itemId?: string) {
    return await actions.updateDirectTask(taskId, {
      employer_name: employerName,
      connector_company_identifier: itemId,
    })
  }

  async function handleCreateTask(employerName: string, itemId?: string) {
    return await actions.createDirectTask({
      employer_name: employerName || '',
      connector_company_identifier: itemId,
    })
  }

  async function selectItem({ item, employerName }: { item?: Item; employerName: string }) {
    setIsLoading(true)

    if (task?.state === DirectTaskState.SKIPPED) {
      await handleUnskipTask(task.id)
    }

    const taskToVerify = task
      ? await handleUpdateTask(task.id, employerName, item?.id)
      : await handleCreateTask(employerName, item?.id)

    if (!taskToVerify) {
      throw new Error('Failed to create task')
    }

    const params = new URLSearchParams({
      employerName,
      q: getSearchQueryUrlParam(employerName, item),
    })

    if (!taskId) {
      navigate(`${location.pathname}/${taskToVerify.id}?${params.toString()}`, {
        replace: true,
        state: location.state,
      })
    }

    verify(taskToVerify.id)
  }

  return { selectItem, isLoading }
}
